import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  ConferenceEvent,
  EventState,
  CreateEventRequest,
  UpdateEventRequest,
  EventListParams,
} from '@/constants/types';
import { eventService } from '@/services/events/eventService';

const initialState: EventState = {
  events: [],
  currentEvent: null,
  pagination: null,
  isLoading: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isEnrolling: false,
  enrollingEventId: null,
  isMarkingInactive: false,
  error: null,
};

export const fetchEvents = createAsyncThunk(
  'events/fetchEvents',
  async (params: EventListParams | undefined, { rejectWithValue }) => {
    try {
      const response = await eventService.getEvents(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch events');
    }
  }
);

export const fetchEvent = createAsyncThunk(
  'events/fetchEvent',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await eventService.getEvent(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch event');
    }
  }
);

export const createEvent = createAsyncThunk(
  'events/createEvent',
  async (data: CreateEventRequest, { rejectWithValue }) => {
    try {
      const response = await eventService.createEvent(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create event');
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async ({ id, data }: { id: string; data: UpdateEventRequest }, { rejectWithValue }) => {
    try {
      const response = await eventService.updateEvent(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update event');
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (id: string, { rejectWithValue }) => {
    try {
      await eventService.deleteEvent(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete event');
    }
  }
);

export const markEventInactive = createAsyncThunk(
  'events/markEventInactive',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await eventService.markInactiveEvent(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark event inactive');
    }
  }
);

export const enrollEvent = createAsyncThunk(
  'events/enrollEvent',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await eventService.enrollEvent(id);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to enroll in event');
    }
  }
);

const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetEventState: (state) => {
      state.events = [];
      state.currentEvent = null;
      state.pagination = null;
      state.error = null;
    },
    setCurrentEvent: (state, action: PayloadAction<ConferenceEvent | null>) => {
      state.currentEvent = action.payload;
    },
    clearEvents: (state) => {
      state.events = [];
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload.events;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchEvent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEvent.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentEvent = action.payload;
      })
      .addCase(fetchEvent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createEvent.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.isCreating = false;
        state.events.unshift(action.payload);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      .addCase(updateEvent.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.events.findIndex((e) => e._id === action.payload._id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        if (state.currentEvent?._id === action.payload._id) {
          state.currentEvent = action.payload;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      .addCase(deleteEvent.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.events = state.events.filter((e) => e._id !== action.payload);
        if (state.currentEvent?._id === action.payload) {
          state.currentEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      .addCase(enrollEvent.pending, (state, action) => {
        state.isEnrolling = true;
        state.enrollingEventId = action.meta.arg;
        state.error = null;
      })
      .addCase(enrollEvent.fulfilled, (state) => {
        state.isEnrolling = false;
        state.enrollingEventId = null;
      })
      .addCase(enrollEvent.rejected, (state, action) => {
        state.isEnrolling = false;
        state.enrollingEventId = null;
        state.error = action.payload as string;
      })
      .addCase(markEventInactive.pending, (state) => {
        state.isMarkingInactive = true;
        state.error = null;
      })
      .addCase(markEventInactive.fulfilled, (state, action) => {
        state.isMarkingInactive = false;
        const index = state.events.findIndex((e) => e._id === action.payload._id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
      })
      .addCase(markEventInactive.rejected, (state, action) => {
        state.isMarkingInactive = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetEventState, setCurrentEvent, clearEvents } = eventSlice.actions;
export default eventSlice.reducer;
